import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Check, Crown, UploadSimple, Gear } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { formatCurrency, formatRelativeTime, formatDate } from "@/lib/format";
import api from "@/lib/api";
import { toast } from "sonner";

const TIER_FEATURES = {
  solo: ["1 account per platform", "Real-time dashboard", "PDF reports", "Autonomous campaign rules", "Email + SMS alerts"],
  agency: ["Up to 5 accounts per platform", "Everything in Solo", "White-label branded reports", "Logo upload", "Priority automation rules"],
};

export default function Billing() {
  const { user, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [tiers, setTiers] = useState({});
  const [subscription, setSubscription] = useState(null);
  const [history, setHistory] = useState([]);
  const [checkingOut, setCheckingOut] = useState(null);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [logoFile, setLogoFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const fetchData = useCallback(async (forceRefresh = false) => {
    const [tiersRes, subRes, histRes] = await Promise.all([
      api.get("/billing/tiers"),
      api.get(forceRefresh ? "/billing/subscription?refresh=true" : "/billing/subscription"),
      api.get("/billing/history"),
    ]);
    setTiers(tiersRes.data);
    setSubscription(subRes.data);
    setHistory(histRes.data);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (searchParams.get("portal_return") !== "true") return;
    fetchData(true).then(() => toast.success("Subscription updated"));
    window.history.replaceState(null, "", "/billing");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) return;

    let attempts = 0;
    const poll = async () => {
      if (attempts >= 5) {
        toast.error("Payment status check timed out. Please refresh in a moment.");
        return;
      }
      attempts += 1;
      try {
        const res = await api.get(`/billing/checkout/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          toast.success(`Subscribed to ${res.data.tier} tier!`);
          const updatedUser = await refreshUser();
          await fetchData();
          window.history.replaceState(null, "", "/billing");
          navigate(updatedUser?.onboarding_complete ? "/dashboard" : "/onboarding");
          return;
        } else if (res.data.status === "expired") {
          toast.error("Checkout session expired");
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        console.error("Failed to poll checkout status:", e);
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleCheckout = async (tier) => {
    setCheckingOut(tier);
    try {
      const res = await api.post("/billing/checkout", { tier, origin_url: window.location.origin, billing_cycle: billingCycle });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error("Failed to start checkout");
      setCheckingOut(null);
    }
  };

  const handleManageSubscription = async () => {
    setOpeningPortal(true);
    try {
      const res = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to open the subscription management portal");
      setOpeningPortal(false);
    }
  };

  const handleLogoUpload = async () => {
    if (!logoFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", logoFile);
      await api.post("/reports/logo", formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to upload logo");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="pt-16">
        <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-6">
          <div>
            <h1 className="font-heading font-semibold text-2xl tracking-tight">Billing</h1>
            <p className="text-sm text-muted-foreground mt-1">Manage your subscription and payment history</p>
          </div>

          {subscription?.tier && (
            <Card className="p-5 flex items-center justify-between flex-wrap gap-3" data-testid="current-subscription-card">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Current Plan</p>
                <div className="font-heading font-semibold text-xl capitalize flex items-center gap-2">
                  {subscription.tier}
                  {subscription.cancel_at_period_end ? (
                    <Badge variant="warning" data-testid="subscription-cancelling-badge">Cancelling</Badge>
                  ) : (
                    <Badge variant="success">Active</Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {formatCurrency(subscription.base_price)}/{subscription.billing_cycle === "annual" ? "yr" : "mo"} + {subscription.ad_spend_pct}% of managed ad spend
                </p>
              </div>
              <div className="flex items-center gap-4">
                <p className="text-sm text-muted-foreground">
                  {subscription.cancel_at_period_end ? "Access ends" : "Renews"} {formatDate(subscription.current_period_end)}
                </p>
                <Button variant="outline" onClick={handleManageSubscription} disabled={openingPortal} data-testid="manage-subscription-button">
                  <Gear className="h-4 w-4 mr-2" />
                  {openingPortal ? "Opening..." : "Manage Subscription"}
                </Button>
              </div>
            </Card>
          )}

          <div className="flex items-center justify-center gap-1 bg-muted rounded-full p-1 w-fit mx-auto" data-testid="billing-cycle-toggle">
            <button
              type="button"
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${billingCycle === "monthly" ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
              onClick={() => setBillingCycle("monthly")}
              data-testid="billing-cycle-monthly-button"
            >
              Monthly
            </button>
            <button
              type="button"
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 ${billingCycle === "annual" ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
              onClick={() => setBillingCycle("annual")}
              data-testid="billing-cycle-annual-button"
            >
              Annual
              <Badge variant="success" className="text-[10px] px-1.5 py-0 leading-4">Save 15%</Badge>
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {Object.entries(tiers).map(([key, tier]) => (
              <Card key={key} className={`p-6 relative ${key === "agency" ? "border-primary/60" : ""}`} data-testid={`billing-tier-card-${key}`}>
                {key === "agency" && <Badge className="absolute -top-3 left-6">Most Popular</Badge>}
                <h3 className="font-heading font-semibold text-xl capitalize">{tier.name}</h3>
                <div className="my-4">
                  <span className="text-3xl font-heading font-bold tracking-tight">
                    {formatCurrency(billingCycle === "annual" ? tier.annual_monthly_equivalent : tier.price)}
                  </span>
                  <span className="text-muted-foreground text-sm">/mo + {tier.ad_spend_pct}% of ad spend</span>
                  {billingCycle === "annual" && (
                    <p className="text-xs text-muted-foreground mt-1" data-testid={`billing-tier-annual-total-${key}`}>
                      Billed {formatCurrency(tier.annual_price)} per year
                    </p>
                  )}
                </div>
                <ul className="space-y-2 mb-6">
                  {TIER_FEATURES[key].map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <Check className="h-4 w-4 text-success shrink-0 mt-0.5" weight="bold" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Button
                  className="w-full"
                  variant={subscription?.tier === key ? "outline" : "default"}
                  disabled={subscription?.tier === key || checkingOut === key}
                  onClick={() => handleCheckout(key)}
                  data-testid={`billing-tier-solo-button-${key}`}
                >
                  {subscription?.tier === key ? "Current Plan" : checkingOut === key ? "Redirecting..." : `Choose ${tier.name}`}
                </Button>
              </Card>
            ))}
          </div>

          {user?.tier === "agency" && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Crown className="h-4 w-4 text-primary" /> White-Label Branding</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="agency-logo">Upload your agency logo</Label>
                  <Input id="agency-logo" type="file" accept="image/*" onChange={(e) => setLogoFile(e.target.files?.[0] || null)} data-testid="agency-logo-input" />
                </div>
                <Button onClick={handleLogoUpload} disabled={!logoFile || uploading} data-testid="upload-logo-button">
                  <UploadSimple className="h-4 w-4 mr-2" />
                  {uploading ? "Uploading..." : "Upload Logo"}
                </Button>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Payment History</CardTitle>
            </CardHeader>
            <CardContent>
              {history.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No transactions yet.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tier</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.map((h) => (
                      <TableRow key={h.id} data-testid={`billing-history-row-${h.id}`}>
                        <TableCell className="capitalize">{h.tier}</TableCell>
                        <TableCell>{formatCurrency(h.amount)}</TableCell>
                        <TableCell>
                          <Badge variant={h.payment_status === "paid" ? "success" : h.payment_status === "expired" ? "secondary" : "warning"} className="capitalize">
                            {h.payment_status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">{formatRelativeTime(h.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
